import { create } from "zustand";
import { persist } from "zustand/middleware";
import { PublicProductDetails } from "interfaces/IProduct";
import { CartItem } from "interfaces/ICart";

interface CartStore {
  items: CartItem[];
  addItem: (
    product: PublicProductDetails,
    colorIndex: number,
    sizeId: number,
    sizeProductId: number
  ) => { success: boolean; message?: string };
  removeItem: (
    productId: number,
    colorIndex: number,
    sizeId: number,
    sizeProductId: number
  ) => void;
  updateQuantity: (
    productId: number,
    colorIndex: number,
    sizeId: number,
    sizeProductId: number,
    quantity: number
  ) => { success: boolean; message?: string };
  clearCart: () => void;
  canAddMore: (
    productId: number,
    colorIndex: number,
    sizeId: number,
    sizeProductId: number,
    currentQuantity: number,
    availableStock: number
  ) => boolean;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (
        product: PublicProductDetails,
        colorIndex,
        sizeId,
        sizeProductId
      ) => {
        const size = product.colors[colorIndex].sizes.find(
          (s) => s.size_id === sizeId
        );
        if (!size) return { success: false, message: "Talla no encontrada" };

        const currentQuantity = get().items.find(
          (item) =>
            item.productId === product.id &&
            item.colorIndex === colorIndex &&
            item.sizeId === sizeId &&
            item.sizeProductId === sizeProductId
        )?.quantity || 0;

        // Validar stock disponible
        if (currentQuantity >= size.stock) {
          return { 
            success: false, 
            message: `No hay más stock disponible. Solo quedan ${size.stock} unidades.` 
          };
        }

        set((state) => {
          const existingItemIndex = state.items.findIndex(
            (item) =>
              item.productId === product.id &&
              item.colorIndex === colorIndex &&
              item.sizeId === sizeId &&
              item.sizeProductId === sizeProductId
          );

          if (existingItemIndex > -1) {
            const newItems = [...state.items];
            newItems[existingItemIndex].quantity += 1;
            return { items: newItems };
          }

          return {
            items: [
              ...state.items,
              {
                productId: product.id,
                colorIndex,
                sizeId,
                quantity: 1,
                price: size.new_price || size.price,
                originalPrice: size.price,
                discount: size.discount || 0,
                discountType: size.discount_type || '',
                finalPrice: size.new_price || size.price,
                name: product.name,
                image: product.colors[colorIndex].images[0],
                color: product.colors[colorIndex].color,
                sizeProductId: sizeProductId,
                colorName: product.colors[colorIndex].colorName,
                sizeName: size.name,
                categories: product.categories,
                stock: size.stock,
              },
            ],
          };
        });

        return { success: true };
      },
      removeItem: (productId, colorIndex, sizeId, sizeProductId) =>
        set((state) => ({
          items: state.items.filter(
            (item) =>
              !(
                item.productId === productId &&
                item.colorIndex === colorIndex &&
                item.sizeId === sizeId &&
                item.sizeProductId === sizeProductId
              )
          ),
        })),
      updateQuantity: (
        productId,
        colorIndex,
        sizeId,
        sizeProductId,
        quantity
      ) => {
        const item = get().items.find(
          (item) =>
            item.productId === productId &&
            item.colorIndex === colorIndex &&
            item.sizeId === sizeId &&
            item.sizeProductId === sizeProductId
        );

        if (!item) {
          return { success: false, message: "Producto no encontrado en el carrito" };
        }

        // Validar que la cantidad no exceda el stock disponible
        if (quantity > item.stock) {
          return { 
            success: false, 
            message: `No hay suficiente stock disponible. Solo quedan ${item.stock} unidades.` 
          };
        }

        // Validar que la cantidad no sea menor a 1
        if (quantity < 1) {
          return { success: false, message: "La cantidad mínima es 1" };
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.productId === productId &&
            item.colorIndex === colorIndex &&
            item.sizeId === sizeId &&
            item.sizeProductId === sizeProductId
              ? { ...item, quantity }
              : item
          ),
        }));

        return { success: true };
      },
      clearCart: () => set({ items: [] }),
      canAddMore: (
        productId,
        colorIndex,
        sizeId,
        sizeProductId,
        currentQuantity,
        availableStock
      ) => {
        return currentQuantity < availableStock;
      },
    }),
    {
      name: "cart-storage",
    }
  )
);
